<template>
    <label
        class="CFormCheckbox"
        :class="[`direction-${direction}`, `label-side-${labelSide}`, { 'checkbox--disabled': disabled }]"
    >
        <input
            v-bind="$attrs"
            :checked="modelValue"
            class="input"
            :class="{ 'hidden': isSwitch, 'input--disabled': disabled }"
            :disabled="disabled"
            :required="required"
            :type="variant === 'radio' ? 'radio' : 'checkbox'"
            v-on="listeners"
            @change="handleChange"
        />

        <div
            v-if="isSwitch"
            class="rounded-full transition-colors duration-150 ease-out"
            :class="[modelValue ? 'bg-primary-500' : 'bg-gray-450 dark:bg-subtle', slim ? 'w-8.5 p-[2px]' : '-mt-px w-9 p-[3px]']"
        >
            <div
                class="aspect-square rounded-full bg-white dark:bg-subtle transition duration-150 ease-out"
                :class="[slim ? 'w-4' : 'w-4.5', { 'translate-x-3.5': modelValue }]"
            ></div>
        </div>

        <div
            v-if="$slots.default"
            class="mx-2"
            :class="[slim ? 'text-sm leading-5' : 'pt-px text-[0.96rem] leading-snug']"
        >
            <span v-if="required" class="text-red-500">*</span>

            <slot />
        </div>
    </label>
</template>

<script setup lang="ts">
import { transformInputListeners } from '@/composables/transformInputListeners'
import { computed, useAttrs } from 'vue'

type Direction = 'row' | 'col'
type Variant = '' | 'switch' | 'radio'
type LabelSide = 'left' | 'right'

interface Props {
    modelValue?: boolean
    required?: boolean
    variant?: Variant
    direction?: Direction
    labelSide?: LabelSide
    slim?: boolean
    disabled?: boolean
}

const props = withDefaults(defineProps<Props>(), {
    modelValue: false,
    required: false,
    variant: '',
    direction: 'row',
    labelSide: 'right',
    slim: false,
    disabled: false
})

const emit = defineEmits(['update:modelValue'])

const attrs = useAttrs()
const listeners = transformInputListeners(attrs)

const isSwitch = computed((): boolean => {
    return props.variant === 'switch'
})

function handleChange(event: Event) {
    const target = event.target as HTMLInputElement
    emit('update:modelValue', target.checked)
}
</script>
